import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {TranslateService} from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../components/confirm-dialog/confirm-dialog.component';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AppState } from '../../store/app.reducer';
import { selectDealerPhoneNumber } from '../../store/dealer/dealer.selectors';
import { countryCd, countryPhoneNumber } from './util.service';

@Injectable({
    providedIn: 'root'
})
export class AlertService {
    private dealerPhoneNumberFromStore = '';
    private readonly panelClass = 'asp-modal-container';
    private readonly confirmDialogTitle = 'generalSettings.alert';

    constructor(private readonly dialog: MatDialog,
                private readonly translate: TranslateService,
                private readonly store: Store<AppState>) {
        this.store.select(selectDealerPhoneNumber).subscribe((phone: string) => {
            this.dealerPhoneNumberFromStore = phone || '';
        });
    }

    showAlert(message: string, value ='', action = '', durationOfPopup = 5000, showPopupTitle = true) {
        const translatedMessage = value !== ''
          ? this.translate.instant(message, { value })
          : this.translate.instant(message);
        const okButton = action || 'teamManagement.ok';

        this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            height: '220px',
            panelClass: this.panelClass,
            data: {
                popupTitle: this.confirmDialogTitle,
                popupBodyText: translatedMessage,
                saveButton: okButton,
                hideCancelButton: true,
                showPopupTitle,
                durationOfPopup
            }
        });
    }

    openRetryPopup(errorMessage: string): Observable<boolean> {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            height: '250px',
            panelClass: this.panelClass,
            data: {
                popupTitle: this.confirmDialogTitle,
                popupBodyText: errorMessage,
                saveButton: 'generalSettings.tryAgain',
                cancelButton: 'generalSettings.cancel',
                showPopupTitle: true
            }
        });

        return dialogRef.afterClosed().pipe(
            map((event) => event?.action === 'confirm')
        );
    }

    showEscalationAlert(): MatDialogRef<ConfirmDialogComponent> {
        const rawPhone = this.dealerPhoneNumberFromStore?.trim() || '';
        const displayPhone = this.formatPhoneByCountry(rawPhone);
        const hrefPhone = this.getDialablePhone(rawPhone);
        const phone = hrefPhone ? `${this.translate.instant('otherTexts.at')}<a class="dealer-contact-link" href="tel:${hrefPhone}"><span class="dealer-info">${displayPhone}</span></a>` : '';
        const escalationBody = `${this.translate.instant('otherTexts.escalationMsg')} ${phone}.`;

        return this.dialog.open(ConfirmDialogComponent, {
            width: '400px',
            height: '250px',
            panelClass: this.panelClass,
            data: {
                popupTitle: this.confirmDialogTitle,
                popupBodyText: escalationBody,
                saveButton: 'teamManagement.ok',
                hideCancelButton: true,
                showPopupTitle: true
            }
        });
    }

    private getDialablePhone(phone: string): string {
        return (phone || '').replace(/[^a-zA-Z\d+]/g, '');
    }

    private formatPhoneByCountry(phone: string): string {
        if (!phone) {
            return '';
        }

        const isUS = countryCd?.toUpperCase().includes('US');
        const cleaned = phone.trim();

        if (isUS) {
            const digits = cleaned.replace(/\D/g, '');
            const localDigits = digits.length === 11 && digits.startsWith('1') ? digits.substring(1) : digits;
            if (localDigits.length === 10) {
                const countryPrefix = countryPhoneNumber(countryCd).replace('+', '') || '1';
                return `${countryPrefix}-${localDigits.substring(0, 3)}-${localDigits.substring(3, 6)}-${localDigits.substring(6)}`;
            }
            return cleaned;
        }

        const alphaNumeric = cleaned.replace(/[^a-zA-Z\d]/g, '').toUpperCase();
        if (alphaNumeric.length >= 3) {
            const prefix = alphaNumeric.substring(0, 3);
            const rest = alphaNumeric.substring(3);
            if (rest.length === 7 && /[A-Z]/.test(rest)) {
                return `(${prefix}) ${rest.substring(0, 5)}-${rest.substring(5)}`;
            }
            if (rest.length >= 7) {
                return `(${prefix}) ${rest.substring(0, 3)}-${rest.substring(3, 7)}`;
            }
        }

        return cleaned;
    }

}